import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { candidaciesMinimalQueryKey } from "@/hooks/queries/useGetCandidaciesMinimal";
import { candidacyInfoQueryKey } from "@/hooks/queries/useGetCandidacyInfo";
import { guacHttpClient } from "@/lib/http";
import {
  CandidacyOwner,
  CandidacyInfoResponse,
  CreateCandidacyOwnerRequest,
  GetOtherCandidaciesResponse,
  HttpResponse,
  MinimalCandidacy,
} from "@/types";

export function useCreateCandidacyOwner() {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      candidacyId,
      createCandidacyOwnerRequest,
    }: {
      candidacyId: string;
      createCandidacyOwnerRequest: CreateCandidacyOwnerRequest;
    }) => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/candidacies/${candidacyId}/candidacy_owners`;
      const res = await guacHttpClient.post<
        CandidacyOwner,
        HttpResponse<CandidacyOwner>,
        CreateCandidacyOwnerRequest
      >(path, createCandidacyOwnerRequest);

      return res.data;
    },
    onMutate: async ({ candidacyId, createCandidacyOwnerRequest }) => {
      // These are the two places where assigning and removing can happen.
      const minimalKey = candidaciesMinimalQueryKey(
        currentCompanyId,
        currentJobId,
      );
      const infoKey = candidacyInfoQueryKey(
        currentCompanyId,
        currentJobId,
        candidacyId,
      );

      await queryClient.cancelQueries({ queryKey: minimalKey });
      await queryClient.cancelQueries({ queryKey: infoKey });
      await queryClient.cancelQueries({ queryKey: ["other-candidacies"] });

      const previousMinimal = queryClient.getQueriesData<MinimalCandidacy[]>({
        queryKey: minimalKey,
      });
      const previousInfo =
        queryClient.getQueryData<CandidacyInfoResponse>(infoKey);
      const previousOtherCandidacies = queryClient.getQueriesData<
        GetOtherCandidaciesResponse[]
      >({
        queryKey: ["other-candidacies"],
      });

      // Single-select: the new owner replaces any existing owner.
      const optimisticOwner: CandidacyOwner = {
        id: -Date.now(),
        candidacyId,
        userId: createCandidacyOwnerRequest.newOwner,
        createdAt: new Date().toISOString(),
      };

      queryClient.setQueriesData<MinimalCandidacy[]>(
        { queryKey: minimalKey },
        (old) =>
          old?.map((c) =>
            c.id === candidacyId ? { ...c, owners: [optimisticOwner] } : c,
          ),
      );

      queryClient.setQueryData<CandidacyInfoResponse>(infoKey, (old) =>
        old
          ? {
              ...old,
              candidacyOwners: [
                { id: optimisticOwner.id, userId: optimisticOwner.userId },
              ],
            }
          : old,
      );
      queryClient.setQueriesData<GetOtherCandidaciesResponse[]>(
        { queryKey: ["other-candidacies"] },
        (old) =>
          old?.map((candidacy) =>
            candidacy.id === candidacyId
              ? { ...candidacy, candidacyOwners: [optimisticOwner] }
              : candidacy,
          ),
      );

      return {
        previousMinimal,
        previousInfo,
        previousOtherCandidacies,
        minimalKey,
        infoKey,
      };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      // Reset on error.
      context.previousMinimal.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      queryClient.setQueryData(context.infoKey, context.previousInfo);
      context.previousOtherCandidacies.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    // Update candidacy owners in line for candidate sheet and pipeline
    onSuccess: (data, variables, context) => {
      queryClient.setQueriesData<MinimalCandidacy[]>(
        { queryKey: context.minimalKey },
        (old) =>
          old?.map((c) =>
            c.id === variables.candidacyId
              ? { ...c, owners: [{ id: data.id, userId: data.userId }] }
              : c,
          ),
      );
      queryClient.setQueryData<CandidacyInfoResponse>(context.infoKey, (old) =>
        old
          ? { ...old, candidacyOwners: [{ id: data.id, userId: data.userId }] }
          : old,
      );
      queryClient.setQueriesData<GetOtherCandidaciesResponse[]>(
        { queryKey: ["other-candidacies"] },
        (old) =>
          old?.map((candidacy) =>
            candidacy.id === variables.candidacyId
              ? { ...candidacy, candidacyOwners: [data] }
              : candidacy,
          ),
      );
    },
    onSettled: () => {
      // Make sure ownership changes reflect in My Aviary
      void queryClient.invalidateQueries({ queryKey: ["my-candidacies"] });
      void queryClient.invalidateQueries({ queryKey: ["other-candidacies"] });
    },
  });

  return mutation;
}
